import React from "react";
import { motion, AnimatePresence } from "framer-motion";

interface ModeSplashTransitionProps {
  active: boolean;
  mode: "everyday" | "memorial" | "kids";
  originRect: DOMRect | null;
  onComplete: () => void;
}

const MODE_COLORS: Record<string, string> = {
  everyday: "hsl(25, 80%, 95%)",
  memorial: "hsl(235, 40%, 96%)",
  kids: "hsl(48, 90%, 92%)",
};

export const ModeSplashTransition: React.FC<ModeSplashTransitionProps> = ({
  active,
  mode,
  originRect,
  onComplete,
}) => {
  React.useEffect(() => {
    if (active) {
      const timer = setTimeout(onComplete, 1100);
      return () => clearTimeout(timer);
    }
  }, [active, onComplete]);

  const cx = originRect ? originRect.left + originRect.width / 2 : window.innerWidth / 2;
  const cy = originRect ? originRect.top + originRect.height / 2 : window.innerHeight / 2;

  // radius needed to cover entire screen from origin point
  const maxR = Math.sqrt(
    Math.max(cx, window.innerWidth - cx) ** 2 +
    Math.max(cy, window.innerHeight - cy) ** 2
  );

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          className="fixed inset-0 z-[9998] flex items-center justify-center"
          style={{ backgroundColor: MODE_COLORS[mode] }}
          initial={{ clipPath: `circle(0px at ${cx}px ${cy}px)` }}
          animate={{ clipPath: `circle(${maxR}px at ${cx}px ${cy}px)` }}
          exit={{ opacity: 0 }}
          transition={{
            clipPath: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] },
            opacity: { duration: 0.35, delay: 0.1 },
          }}
        >
          {/* Paw icon fade-in then fade-out */}
          <motion.span
            className="text-5xl"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: [0, 1, 1, 0], scale: [0.6, 1, 1, 0.95] }}
            transition={{ duration: 1.0, times: [0, 0.25, 0.7, 1], ease: "easeInOut" }}
          >
            🐾
          </motion.span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
